import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timegridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';

import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { DataView } from 'primereact/dataview';

import NavBar from '../../componentes/Navbar';
import AtividadeInclusaoModal from '../../componentes/AtividadeInclusaoModal';
import AgendaService from '../../app/service/AgendaService';
import GrupoTrabalhoService from '../../app/service/GrupoTrabalhoService';
import UsuarioService from '../../app/service/UsuarioService';
import StatusAtividadeService from '../../app/service/StatusAtividadeService';
import { AuthContext } from '../../main/ProvedorAutenticacao';
import AtividadeComponent from './AtividadeComponent';

import './agenda.css';

const agendaService          = new AgendaService();
const grupoTrabalhoService   = new GrupoTrabalhoService();
const usuarioService         = new UsuarioService();
const statusAtividadeService = new StatusAtividadeService();

const TIPOS_ATIVIDADE = [
  { codigo: 'A', nome: 'Compromissos/Audiências' },
  { codigo: 'T', nome: 'Prazos' },
];

function periodoAtual() {
  const hoje = new Date();
  const ano  = hoje.getFullYear();
  const mes  = String(hoje.getMonth() + 1).padStart(2, '0');
  const dias = new Date(ano, hoje.getMonth() + 1, 0).getDate();
  return { dataInicio: `${ano}-${mes}-01`, dataFim: `${ano}-${mes}-${dias}` };
}

export default function Agenda() {
  const { usuarioAutenticado } = useContext(AuthContext);
  const toast = useRef(null);
  const calendarioIniciado = useRef(false);

  const [carregando, setCarregando]               = useState(true);
  const [calendarioMontado, setCalendarioMontado] = useState(false);

  // dados dos filtros
  const [grupos, setGrupos]                         = useState([]);
  const [gruposLista, setGruposLista]               = useState([]);
  const [usuarios, setUsuarios]                     = useState([]);
  const [usuariosLista, setUsuariosLista]           = useState([]);
  const [statusOpcoes, setStatusOpcoes]             = useState([]);
  const [selectStatus, setSelectStatus]             = useState([]);
  const [tiposConsulta, setTiposConsulta]           = useState(TIPOS_ATIVIDADE);

  // eventos do calendário
  const [agenda, setAgenda] = useState([]);

  // modais
  const [modalNovaAtividade, setModalNovaAtividade]     = useState(false);
  const [novaAtividadeData, setNovaAtividadeData]       = useState(null);
  const [modalEditarAtividade, setModalEditarAtividade] = useState(false);
  const [codigoAtividade, setCodigoAtividade]           = useState(null);

  // refs para acessar estado atual dentro de callbacks do FullCalendar
  const gruposListaRef    = useRef(gruposLista);
  const usuariosListaRef  = useRef(usuariosLista);
  const selectStatusRef   = useRef(selectStatus);
  const tiposConsultaRef  = useRef(tiposConsulta);
  const ultimoRangeRef    = useRef(null);

  useEffect(() => { gruposListaRef.current   = gruposLista;   }, [gruposLista]);
  useEffect(() => { usuariosListaRef.current = usuariosLista; }, [usuariosLista]);
  useEffect(() => { selectStatusRef.current  = selectStatus;  }, [selectStatus]);
  useEffect(() => { tiposConsultaRef.current = tiposConsulta; }, [tiposConsulta]);

  const consultarAgenda = useCallback(async (dataInicio, dataFim) => {
    const gruposAtual   = gruposListaRef.current;
    const usuariosAtual = usuariosListaRef.current;
    const statusAtual   = selectStatusRef.current;
    const tiposAtual    = tiposConsultaRef.current;

    const codigoGrupos    = gruposAtual.map(g => g.codigo).join(',');
    const codigosStatus   = statusAtual.map(s => s.codigo).join(',');
    const codigoTipos     = tiposAtual.map(t => `'${t.codigo}'`).join(',');
    const codigosUsuarios = usuarioAutenticado.perfil.codigo !== 1
      ? usuarioAutenticado.codigo
      : usuariosAtual.map(u => u.codigo).join(',');

    const { dataInicio: inicio, dataFim: fim } = dataInicio && dataFim
      ? { dataInicio, dataFim }
      : periodoAtual();

    const filtro = {
      usuario: usuarioAutenticado.codigo,
      usuarios: codigosUsuarios,
      grupos: codigoGrupos,
      status: codigosStatus,
      tipos: codigoTipos,
      dataInicio: inicio,
      dataFim: fim,
    };

    try {
      const response = await agendaService.consultarAgenda(filtro);
      setAgenda(response.data);
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Agenda', detail: `${error}`, life: 4000 });
    }
  }, [usuarioAutenticado]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    async function carregarDados() {
      try {
        const [gruposResp, statusResp] = await Promise.all([
          grupoTrabalhoService.listarGruposUsuario2(usuarioAutenticado.codigo),
          statusAtividadeService.listarStatus(usuarioAutenticado.empresa.codigo),
        ]);

        let usuariosCarregados = [];
        if (usuarioAutenticado.perfil.codigo !== 1) {
          usuariosCarregados = [{ codigo: usuarioAutenticado.codigo, nome: usuarioAutenticado.nome }];
        } else {
          const usuariosResp = await usuarioService.listarUsuariosEmpresa(usuarioAutenticado.empresa.codigo);
          usuariosCarregados = usuariosResp.data;
        }

        const gruposCarregados = gruposResp.data;
        const statusCarregados = statusResp.data;

        gruposListaRef.current   = gruposCarregados;
        usuariosListaRef.current = usuariosCarregados;
        selectStatusRef.current  = statusCarregados;

        setGrupos(gruposCarregados);
        setGruposLista(gruposCarregados);
        setUsuarios(usuariosCarregados);
        setUsuariosLista(usuariosCarregados);
        setStatusOpcoes(statusCarregados);
        setSelectStatus(statusCarregados);

        await consultarAgenda();
      } catch (error) {
        toast.current.show({ severity: 'error', summary: 'Agenda', detail: `Erro ao carregar dados`, life: 4000 });
      } finally {
        setCarregando(false);
        setCalendarioMontado(true);
      }
    }

    carregarDados();
  }, []);

  async function filtrar() {
    await consultarAgenda();
  }

  const onDatesSet = useCallback(async (dateInfo) => {
    if (!calendarioIniciado.current) {
      calendarioIniciado.current = true;
      return;
    }
    const inicio = dateInfo.startStr.substring(0, 10);
    const fim    = dateInfo.endStr.substring(0, 10);
    if (ultimoRangeRef.current?.inicio === inicio && ultimoRangeRef.current?.fim === fim) {
      return;
    }
    ultimoRangeRef.current = { inicio, fim };
    await consultarAgenda(inicio, fim);
  }, [consultarAgenda]);

  const abrirNovaAtividade = useCallback((dateInfo) => {
    setNovaAtividadeData(dateInfo.date);
    setModalNovaAtividade(true);
  }, []);

  const abrirAtividade = useCallback((eventInfo) => {
    setCodigoAtividade(eventInfo.event.id);
    setModalEditarAtividade(true);
  }, []);

  function tagStatus(status) {
    return (
      <>
        &nbsp;
        <Tag
          style={{ padding: '12px', backgroundColor: status.cor }}
          value={status.status}
        />
      </>
    );
  }

  return (
    <>
      <NavBar />
      <Toast ref={toast} />

      <div className="content-wrapper">
        <section className="content-header">
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12">
                <div className="card card-primary card-outline">

                  <div className="card-header">
                    <h3 className="card-title">
                      <i className="fas fa-stream" />
                      <span> Agenda de Compromissos</span>
                      <br /><br />
                      <DataView
                        value={statusOpcoes}
                        itemTemplate={tagStatus}
                        layout="grid"
                      />
                    </h3>
                  </div>

                  <div className="card-body">
                    <div className="row mb-2">
                      <div className="col-md-3">
                        <MultiSelect
                          value={gruposLista}
                          options={grupos}
                          style={{ width: '100%', height: '32px' }}
                          onChange={e => setGruposLista(e.value)}
                          filter showClear filterBy="nome"
                          optionLabel="nome"
                          placeholder="Grupos de Trabalho"
                        />
                      </div>
                      <div className="col-md-3">
                        <MultiSelect
                          value={usuariosLista}
                          options={usuarios}
                          style={{ width: '100%', height: '32px' }}
                          onChange={e => setUsuariosLista(e.value)}
                          filter showClear filterBy="nome"
                          optionLabel="nome"
                          placeholder="Usuários"
                        />
                      </div>
                      <div className="col-md-3">
                        <MultiSelect
                          value={selectStatus}
                          options={statusOpcoes}
                          style={{ width: '100%', height: '32px' }}
                          onChange={e => setSelectStatus(e.value)}
                          filter showClear filterBy="status"
                          optionLabel="status"
                          placeholder="Status"
                        />
                      </div>
                      <div className="col-md-2">
                        <MultiSelect
                          value={tiposConsulta}
                          options={TIPOS_ATIVIDADE}
                          style={{ width: '100%', height: '32px' }}
                          onChange={e => setTiposConsulta(e.value)}
                          optionLabel="nome"
                          placeholder="Tipos"
                        />
                      </div>
                      <div className="col-md-1">
                        <Button
                          style={{ height: '32px' }}
                          type="button"
                          className="p-button-secondary p-button-text p-button-sm"
                          onClick={filtrar}
                        >
                          <i className="pi pi-filter" /> &nbsp;&nbsp;Filtrar
                        </Button>
                      </div>
                    </div>

                    {calendarioMontado && (
                      <FullCalendar
                        plugins={[dayGridPlugin, interactionPlugin, timegridPlugin]}
                        initialView="dayGridMonth"
                        editable={true}
                        selectable={true}
                        locales={[ptBrLocale]}
                        locale="pt-br"
                        selectMirror={true}
                        dayMaxEvents={true}
                        height="auto"
                        events={agenda}
                        eventClick={abrirAtividade}
                        dateClick={abrirNovaAtividade}
                        datesSet={onDatesSet}
                      />
                    )}
                  </div>

                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {carregando && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div className="spinner-border text-light" style={{ width: '4rem', height: '4rem' }} role="status" />
        </div>
      )}

      <AtividadeInclusaoModal
        key={modalNovaAtividade ? 'atividade-open' : 'atividade-closed'}
        visible={modalNovaAtividade}
        dtLimiteInicial={novaAtividadeData}
        onHide={() => setModalNovaAtividade(false)}
        onCadastrado={() => {
          setModalNovaAtividade(false);
          consultarAgenda();
        }}
      />

      <AtividadeComponent
        visible={modalEditarAtividade}
        atividade={codigoAtividade}
        onHide={() => setModalEditarAtividade(false)}
        onAtividadeAlterada={() => {
          setModalEditarAtividade(false);
          consultarAgenda();
        }}
      />
    </>
  );
}
